import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  Button,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import Papa from "papaparse";
import { useBulkImportProductsMutation } from "state/api";
import { PageContainer, DataTable, StatusChip } from "components/shared";

const TEMPLATE_COLUMNS = [
  "productCode", "name", "description", "category", "subCategory", "brand",
  "purchasePrice", "sellingPrice", "stock", "minStock", "unit", "barcode", "tax", "status",
];
const TEMPLATE_EXAMPLE = [
  "PRD-1001", "Sample Product", "A great product", "Electronics", "Mobiles", "Samsung",
  "1000", "1499", "50", "10", "pieces", "8901234567890", "18", "active",
];

const PREVIEW_COLUMNS = [
  { field: "productCode", headerName: "Code", render: (r) => r.productCode || "—" },
  { field: "name", headerName: "Name", render: (r) => r.name || "—" },
  { field: "category", headerName: "Category", render: (r) => r.category || "—" },
  { field: "sellingPrice", headerName: "Sell Price", align: "right", render: (r) => r.sellingPrice || "—" },
  { field: "stock", headerName: "Stock", align: "right", render: (r) => r.stock || "—" },
];

const BulkImport = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [result, setResult] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const [bulkImport, { isLoading }] = useBulkImportProductsMutation();

  const parseFile = (file) => {
    setResult(null);
    setParseError("");
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const cleaned = (res.data || []).filter((r) => r.productCode || r.name);
        setRows(cleaned);
        if (!cleaned.length) setParseError("No data rows found in the file.");
      },
      error: (err) => setParseError(err.message),
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    parseFile(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    setSelectedFile(file);
    parseFile(file);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setRows([]);
    setResult(null);
    setParseError("");
  };

  const handleSubmit = async () => {
    if (!rows.length) return;
    try {
      const res = await bulkImport(rows).unwrap();
      setResult(res);
      setSnack({
        open: true,
        severity: res?.failedCount ? "warning" : "success",
        message: `Imported ${res?.createdCount ?? 0} product(s).${
          res?.failedCount ? ` ${res.failedCount} failed.` : ""
        }`,
      });
    } catch (err) {
      const message = err?.data?.error?.message || err?.data?.message || "Import failed";
      setParseError(message);
      setSnack({ open: true, severity: "error", message });
    }
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: TEMPLATE_COLUMNS, data: [TEMPLATE_EXAMPLE] });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer
      title="Bulk Import Products"
      subtitle="Upload a CSV to create many products at once"
      maxWidth={900}
    >
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Instructions
      </Typography>
      <Box component="ol" sx={{ color: "text.secondary", pl: 3, mt: 0 }}>
        <li>Download the template CSV and fill it with your products.</li>
        <li>
          <b>category</b> must be one of: Electronics, Clothing, Accessories, Home &amp;
          Living, Books, Others.
        </li>
        <li>
          <b>productCode</b> must be unique. purchasePrice, sellingPrice and stock are
          required numbers.
        </li>
        <li>Upload the completed CSV below, review the preview, and click Submit.</li>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Link component="button" type="button" onClick={downloadTemplate} underline="hover">
          Download CSV Template
        </Link>
      </Box>

      <Card
        variant="outlined"
        sx={{
          mt: 3, p: 4, textAlign: "center", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", borderStyle: "dashed",
          bgcolor: "background.paper", cursor: "pointer", borderRadius: "12px",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
        onDrop={handleFileDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById("fileInput").click()}
      >
        <CloudUpload fontSize="large" color="primary" />
        <Typography variant="body1" mt={1}>
          Drag &amp; Drop or Browse CSV File
        </Typography>
        {selectedFile && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {selectedFile.name} — {rows.length} row(s) parsed
          </Typography>
        )}
      </Card>
      <input
        id="fileInput"
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}

      {result && (
        <Alert severity={result.failedCount ? "warning" : "success"} sx={{ mt: 2 }}>
          Imported {result.createdCount} product(s). {result.failedCount || 0} failed.
        </Alert>
      )}

      {rows.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview ({rows.length} rows)
          </Typography>
          <DataTable
            columns={PREVIEW_COLUMNS}
            rows={rows.slice(0, 100)}
            emptyText="No rows parsed"
          />
        </Box>
      )}

      {result?.failed?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Failed rows
          </Typography>
          <DataTable
            columns={[
              { field: "row", headerName: "Row", render: (f) => f.row },
              { field: "productCode", headerName: "Product Code", render: (f) => f.productCode || "—" },
              {
                field: "error",
                headerName: "Error",
                render: (f) => <StatusChip status="failed" label={f.error} />,
              },
            ]}
            rows={result.failed}
            getRowId={(f) => `fail-${f.row}`}
          />
        </Box>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" color="error" onClick={handleReset}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !rows.length}
        >
          {isLoading ? <CircularProgress size={20} /> : "Submit"}
        </Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default BulkImport;
